import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  ArrowLeft,
  Radar,
  Globe,
  Upload,
  Plus,
  Pencil,
  Trash2,
  EyeOff,
  Eye,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Sparkles,
  Search,
  FileText,
  Calendar,
  MapPin,
  ExternalLink,
  Power,
  Bot,
  Send,
  Lightbulb,
  Download,
  Bell,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCORE_STYLES = {
  exceptional: { label: 'Offerta Eccezionale', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  great: { label: 'Ottima Occasione', cls: 'bg-green-100 text-green-700 border-green-300' },
  good: { label: 'Buona Offerta', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  average: { label: 'Prezzo nella Media', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  poor: { label: 'Poco Conveniente', cls: 'bg-red-100 text-red-700 border-red-300' },
};

const formatEUR = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);

const formatDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('it-IT');
  } catch {
    return s;
  }
};

const TravelRadar = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('offers');
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);

  // Offers
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [filters, setFilters] = useState({ category: 'all', search: '', includeHidden: false });
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  // Sources
  const [sources, setSources] = useState([]);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [sourceForm, setSourceForm] = useState({
    name: '',
    url: '',
    category: 'crociere',
    frequency_hours: 24,
    enabled: true,
    notes: '',
  });

  // Import
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);

  // AI Assistant
  const [aiQuery, setAiQuery] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiHistorySearch, setAiHistorySearch] = useState('');
  const [aiTab, setAiTab] = useState('new'); // 'new' | 'history' | 'analytics'
  const [aiAnalytics, setAiAnalytics] = useState(null);
  const [aiAnalyticsPeriod, setAiAnalyticsPeriod] = useState('all');

  // Customer Alerts
  const [alerts, setAlerts] = useState([]);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const EMPTY_ALERT = {
    client_name: '',
    client_phone: '',
    client_email: '',
    category: '',
    destination_contains: '',
    max_price: '',
    departure_from_date: '',
    departure_to_date: '',
    notes: '',
    active: true,
  };
  const [alertForm, setAlertForm] = useState(EMPTY_ALERT);
  const [alertMatches, setAlertMatches] = useState({}); // alert_id → {match_count, matches[]}
  const [matchesDialog, setMatchesDialog] = useState(null); // alert obj when viewing matches // 'all' | '7d' | '30d' | '90d'

  // Offer form
  const EMPTY_OFFER = {
    title: '',
    category: 'crociere',
    destination: '',
    departure_date: '',
    return_date: '',
    duration_nights: '',
    departure_location: '',
    structure_name: '',
    board_type: '',
    current_price: '',
    source_name: '',
    source_url: '',
    image_url: '',
    availability: '',
    conditions: '',
    notes: '',
  };
  const [offerForm, setOfferForm] = useState(EMPTY_OFFER);

  useEffect(() => {
    fetchCategories();
    fetchStats();
    fetchOffers();
    fetchSources();
    fetchAiHistory();
    fetchAlerts();
  }, []);

  useEffect(() => {
    if (aiTab === 'analytics') fetchAiAnalytics();
  }, [aiTab, aiAnalyticsPeriod]);

  useEffect(() => {
    fetchOffers();
  }, [filters.category, filters.includeHidden]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/travel-radar/categories`);
      setCategories(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/travel-radar/stats`);
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchOffers = async () => {
    setLoadingOffers(true);
    try {
      const params = {};
      if (filters.category && filters.category !== 'all') params.category = filters.category;
      if (filters.search) params.search = filters.search;
      if (filters.includeHidden) params.include_hidden = true;
      const res = await axios.get(`${API}/travel-radar/offers`, { params });
      setOffers(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Errore caricamento offerte');
    } finally {
      setLoadingOffers(false);
    }
  };

  const fetchSources = async () => {
    try {
      const res = await axios.get(`${API}/travel-radar/sources`);
      setSources(res.data || []);
    } catch (e) { console.error(e); }
  };

  // --- Sources actions ---
  const openCreateSource = () => {
    setEditingSource(null);
    setSourceForm({ name: '', url: '', category: 'crociere', frequency_hours: 24, enabled: true, notes: '' });
    setShowSourceDialog(true);
  };

  const openEditSource = (src) => {
    setEditingSource(src.id);
    setSourceForm({
      name: src.name || '',
      url: src.url || '',
      category: src.category || 'crociere',
      frequency_hours: src.frequency_hours || 24,
      enabled: src.enabled !== false,
      notes: src.notes || '',
    });
    setShowSourceDialog(true);
  };

  const saveSource = async () => {
    if (!sourceForm.name.trim() || !sourceForm.url.trim()) {
      toast.error('Nome e URL sono obbligatori');
      return;
    }
    try {
      if (editingSource) {
        await axios.put(`${API}/travel-radar/sources/${editingSource}`, sourceForm);
        toast.success('Fonte aggiornata');
      } else {
        await axios.post(`${API}/travel-radar/sources`, sourceForm);
        toast.success('Fonte aggiunta');
      }
      setShowSourceDialog(false);
      fetchSources();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const deleteSource = async (id) => {
    if (!window.confirm('Eliminare questa fonte?')) return;
    try {
      await axios.delete(`${API}/travel-radar/sources/${id}`);
      toast.success('Fonte eliminata');
      fetchSources();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const toggleSource = async (src) => {
    try {
      await axios.put(`${API}/travel-radar/sources/${src.id}`, { enabled: !src.enabled });
      fetchSources();
    } catch (e) { toast.error('Errore'); }
  };

  // --- Offer actions ---
  const openCreateOffer = () => {
    setEditingOffer(null);
    setOfferForm(EMPTY_OFFER);
    setShowOfferDialog(true);
  };

  const openEditOffer = (o) => {
    setEditingOffer(o.id);
    setOfferForm({
      ...EMPTY_OFFER,
      ...o,
      departure_date: o.departure_date ? new Date(o.departure_date).toISOString().slice(0, 10) : '',
      return_date: o.return_date ? new Date(o.return_date).toISOString().slice(0, 10) : '',
      duration_nights: o.duration_nights || '',
      current_price: o.current_price || '',
    });
    setShowOfferDialog(true);
  };

  const saveOffer = async () => {
    if (!offerForm.title.trim() || !offerForm.category) {
      toast.error('Titolo e categoria obbligatori');
      return;
    }
    const payload = {
      ...offerForm,
      current_price: parseFloat(offerForm.current_price) || 0,
      duration_nights: offerForm.duration_nights ? parseInt(offerForm.duration_nights, 10) : null,
      departure_date: offerForm.departure_date ? new Date(offerForm.departure_date).toISOString() : null,
      return_date: offerForm.return_date ? new Date(offerForm.return_date).toISOString() : null,
    };
    try {
      if (editingOffer) {
        await axios.put(`${API}/travel-radar/offers/${editingOffer}`, payload);
        toast.success('Offerta aggiornata');
      } else {
        await axios.post(`${API}/travel-radar/offers`, payload);
        toast.success('Offerta aggiunta');
      }
      setShowOfferDialog(false);
      fetchOffers();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const hideOffer = async (id) => {
    try {
      await axios.post(`${API}/travel-radar/offers/${id}/hide`);
      toast.success('Offerta nascosta');
      fetchOffers();
    } catch (e) { toast.error('Errore'); }
  };

  const unhideOffer = async (id) => {
    try {
      await axios.post(`${API}/travel-radar/offers/${id}/unhide`);
      toast.success('Offerta ripristinata');
      fetchOffers();
    } catch (e) { toast.error('Errore'); }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm('Eliminare definitivamente questa offerta?')) return;
    try {
      await axios.delete(`${API}/travel-radar/offers/${id}`);
      toast.success('Offerta eliminata');
      fetchOffers();
      fetchStats();
    } catch (e) { toast.error('Errore'); }
  };

  const applyToQuote = (o) => {
    // Pre-fill query string for QuoteManager Create flow (simple approach)
    const params = new URLSearchParams({
      from_radar: '1',
      title: o.title || '',
      destination: o.destination || '',
      price: o.current_price || '',
      nights: o.duration_nights || '',
      departure: o.departure_date || '',
    });
    window.location.href = `/quotes?${params.toString()}`;
  };

  // --- AI Assistant ---
  const fetchAiHistory = async () => {
    try {
      const res = await axios.get(`${API}/travel-radar/ai/history`);
      setAiHistory(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchAiAnalytics = async () => {
    try {
      const params = aiAnalyticsPeriod !== 'all' ? { period: aiAnalyticsPeriod } : {};
      const res = await axios.get(`${API}/travel-radar/ai/analytics`, { params });
      setAiAnalytics(res.data);
    } catch (e) { console.error(e); }
  };

  const exportAiHistoryExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = aiAnalyticsPeriod !== 'all' ? `?period=${aiAnalyticsPeriod}` : '';
      const res = await axios.get(`${API}/travel-radar/ai/history/export/excel${params}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storico_ai_${aiAnalyticsPeriod}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel scaricato');
    } catch (e) {
      toast.error('Errore export');
    }
  };

  const exportAnalyticsCSV = () => {
    if (!aiAnalytics) return;
    const lines = [];
    lines.push('Sezione,Voce,Conteggio,Valore extra');
    lines.push(`Totali,Richieste totali,${aiAnalytics.total_requests},`);
    lines.push(`Totali,Lead caldi,${aiAnalytics.hot_leads},${aiAnalytics.hot_leads_pct}%`);
    lines.push(`Totali,Lead freddi,${aiAnalytics.cold_leads},`);
    lines.push(`Totali,Bambini età mancante,${aiAnalytics.bambini_eta_mancante},`);
    lines.push(`Totali,Budget medio,${aiAnalytics.avg_budget || ''},`);
    lines.push(`Totali,Budget mediana,${aiAnalytics.median_budget || ''},`);
    lines.push(`Totali,Conversion con suggerimenti,${aiAnalytics.suggestions_given},${aiAnalytics.conversion_with_suggestions_pct}%`);
    aiAnalytics.top_categories.forEach((c) => {
      lines.push(`Categoria,"${c.tipologia}",${c.count},${c.avg_budget || ''}`);
    });
    aiAnalytics.top_destinations.forEach((d) => {
      lines.push(`Destinazione,"${d.destination}",${d.count},`);
    });
    aiAnalytics.top_departures.forEach((d) => {
      lines.push(`Partenza,"${d.departure}",${d.count},`);
    });
    const csv = lines.join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_ai_${aiAnalyticsPeriod}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('CSV scaricato');
  };

  const askAI = async () => {
    if (!aiQuery.trim()) {
      toast.error('Descrivi la richiesta del cliente');
      return;
    }
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await axios.post(`${API}/travel-radar/ai/analyze`, {
        client_request: aiQuery,
        max_suggestions: 5,
      });
      setAiResult(res.data);
      if (!res.data?.suggestions?.length) {
        toast.warning('Nessun match nel catalogo Radar — prova ad aggiungere offerte');
      } else {
        toast.success(`AI ha trovato ${res.data.suggestions.length} offerte`);
      }
      fetchAiHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore AI');
    } finally {
      setAiLoading(false);
    }
  };

  const loadAiHistoryDetail = async (entryId) => {
    try {
      const res = await axios.get(`${API}/travel-radar/ai/history/${entryId}`);
      setAiQuery(res.data.client_request || '');
      setAiResult({
        analysis: res.data.analysis,
        extracted_criteria: res.data.extracted_criteria || {},
        suggestions: res.data.suggestions || [],
      });
      setAiTab('new');
      toast.success('Analisi ricaricata');
    } catch (e) { toast.error('Errore caricamento'); }
  };

  const deleteAiHistory = async (entryId) => {
    if (!window.confirm('Eliminare questa richiesta dallo storico?')) return;
    try {
      await axios.delete(`${API}/travel-radar/ai/history/${entryId}`);
      toast.success('Eliminata');
      fetchAiHistory();
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  // --- Customer Alerts ---
  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/travel-radar/alerts`);
      setAlerts(res.data || []);
      // For each alert, fetch match count
      (res.data || []).forEach((a) => fetchAlertMatches(a.id));
    } catch (e) { console.error(e); }
  };

  const fetchAlertMatches = async (alertId) => {
    try {
      const res = await axios.get(`${API}/travel-radar/alerts/${alertId}/matches`);
      setAlertMatches((prev) => ({ ...prev, [alertId]: res.data }));
    } catch (e) { /* ignore */ }
  };

  const openCreateAlert = () => {
    setEditingAlert(null);
    setAlertForm(EMPTY_ALERT);
    setShowAlertDialog(true);
  };

  const openEditAlert = (a) => {
    setEditingAlert(a.id);
    setAlertForm({
      ...EMPTY_ALERT,
      ...a,
      category: a.category || '',
      max_price: a.max_price || '',
      departure_from_date: a.departure_from_date ? new Date(a.departure_from_date).toISOString().slice(0, 10) : '',
      departure_to_date: a.departure_to_date ? new Date(a.departure_to_date).toISOString().slice(0, 10) : '',
    });
    setShowAlertDialog(true);
  };

  const saveAlert = async () => {
    if (!alertForm.client_name.trim() || !alertForm.max_price) {
      toast.error('Nome cliente e prezzo massimo sono obbligatori');
      return;
    }
    const payload = {
      ...alertForm,
      category: alertForm.category || null,
      max_price: parseFloat(alertForm.max_price),
      departure_from_date: alertForm.departure_from_date ? new Date(alertForm.departure_from_date).toISOString() : null,
      departure_to_date: alertForm.departure_to_date ? new Date(alertForm.departure_to_date).toISOString() : null,
    };
    try {
      if (editingAlert) {
        await axios.put(`${API}/travel-radar/alerts/${editingAlert}`, payload);
        toast.success('Alert aggiornato');
      } else {
        await axios.post(`${API}/travel-radar/alerts`, payload);
        toast.success('Alert creato');
      }
      setShowAlertDialog(false);
      fetchAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const toggleAlert = async (a) => {
    try {
      await axios.put(`${API}/travel-radar/alerts/${a.id}`, { active: !a.active });
      fetchAlerts();
    } catch (e) { toast.error('Errore'); }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Eliminare questo alert?')) return;
    try {
      await axios.delete(`${API}/travel-radar/alerts/${id}`);
      toast.success('Alert eliminato');
      fetchAlerts();
    } catch (e) { toast.error('Errore'); }
  };

  const openMatchesDialog = async (a) => {
    await fetchAlertMatches(a.id);
    setMatchesDialog(a);
  };

  const runAlertCheck = async () => {
    try {
      const res = await axios.post(`${API}/travel-radar/alerts/run-check`);
      toast.success(`Check: ${res.data.checked_alerts} alert verificati, ${res.data.total_matches} match totali`);
      fetchAlerts();
    } catch (e) { toast.error('Errore check'); }
  };

  // --- Bulk import ---
  const handleImport = async () => {
    if (!importText.trim()) {
      toast.error('Incolla del JSON con le offerte');
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch (e) {
      toast.error('JSON non valido');
      return;
    }
    const offersArray = Array.isArray(parsed) ? parsed : parsed.offers;
    if (!Array.isArray(offersArray)) {
      toast.error('Formato non valido: serve un array di offerte o {"offers":[...]}');
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${API}/travel-radar/offers/bulk-import`, { offers: offersArray });
      const { created, updated, skipped, total } = res.data;
      toast.success(`Importate ${total} offerte (${created} nuove, ${updated} aggiornate)`);
      if (skipped?.length > 0) {
        toast.warning(`${skipped.length} saltate — controlla i dati`);
      }
      setImportText('');
      fetchOffers();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore importazione');
    } finally {
      setImporting(false);
    }
  };

  // Filter offers locally for search (refetch on category)
  const filteredOffers = filters.search.trim()
    ? offers.filter((o) =>
        [o.title, o.destination, o.structure_name, o.departure_location]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(filters.search.toLowerCase()))
      )
    : offers;

  if (!['admin', 'agent'].includes(user?.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card><CardHeader><CardTitle>Accesso negato</CardTitle></CardHeader></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="travel-radar-page">
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Dashboard
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Radar className="w-6 h-6 text-indigo-600" />
                <h1 className="text-lg sm:text-xl font-bold text-slate-800">Travel Radar AI</h1>
                <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 text-xs">MVP</Badge>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-indigo-100">Offerte attive</div>
                <div className="text-3xl font-bold mt-1">{stats.total_offers}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-blue-100">Fonti configurate</div>
                <div className="text-3xl font-bold mt-1">{stats.total_sources}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-emerald-100">Categorie monitorate</div>
                <div className="text-3xl font-bold mt-1">{stats.by_category?.length || 0}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-amber-100">Top score</div>
                <div className="text-3xl font-bold mt-1">
                  {stats.top_offers?.[0]?.convenience_score?.toFixed(0) ?? '—'}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full md:w-auto md:inline-flex">
            <TabsTrigger value="offers" data-testid="tab-offers">
              <Sparkles className="w-4 h-4 mr-1" /> Offerte
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">
              <Bot className="w-4 h-4 mr-1" /> AI Assistant
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">
              <Bell className="w-4 h-4 mr-1" /> Alert Clienti
            </TabsTrigger>
            <TabsTrigger value="sources" data-testid="tab-sources">
              <Globe className="w-4 h-4 mr-1" /> Fonti URL
            </TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">
              <Upload className="w-4 h-4 mr-1" /> Importa
            </TabsTrigger>
          </TabsList>

          {/* OFFERS TAB */}
          <TabsContent value="offers" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <CardTitle>Offerte Radar</CardTitle>
                    <CardDescription>
                      Offerte aggregate ordinate per convenienza
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => { fetchOffers(); fetchStats(); }}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
                    </Button>
                    <Button onClick={openCreateOffer} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-offer-btn">
                      <Plus className="w-4 h-4 mr-1" /> Nuova Offerta
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cerca titolo, destinazione, struttura..."
                      className="pl-9"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      data-testid="filter-search"
                    />
                  </div>
                  <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                    <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Tutte le categorie" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le categorie</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant={filters.includeHidden ? 'default' : 'outline'}
                    onClick={() => setFilters({ ...filters, includeHidden: !filters.includeHidden })}
                    data-testid="toggle-hidden"
                  >
                    <EyeOff className="w-4 h-4 mr-1" />
                    {filters.includeHidden ? 'Mostra anche nascoste ✓' : 'Mostra anche nascoste'}
                  </Button>
                </div>

                {loadingOffers ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                  </div>
                ) : filteredOffers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="offers-empty">
                    <Radar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Nessuna offerta nel radar</p>
                    <p className="text-sm">Aggiungi manualmente o importa via JSON.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredOffers.map((o) => {
                      const style = SCORE_STYLES[o.convenience_label] || SCORE_STYLES.average;
                      const catLabel = categories.find((c) => c.value === o.category)?.label || o.category;
                      const priceDelta =
                        o.previous_price && o.previous_price !== o.current_price
                          ? o.current_price - o.previous_price
                          : 0;
                      return (
                        <div
                          key={o.id}
                          data-testid={`offer-card-${o.id}`}
                          className={`border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow ${o.is_hidden ? 'opacity-60' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold text-slate-800 truncate">{o.title}</h4>
                                {o.is_hidden && <Badge variant="outline" className="text-xs">Nascosta</Badge>}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                                <Badge variant="secondary" className="text-xs">{catLabel}</Badge>
                                {o.destination && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />{o.destination}
                                  </span>
                                )}
                                {o.duration_nights && <span>{o.duration_nights} notti</span>}
                                {o.departure_date && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />{formatDate(o.departure_date)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className={`text-xs px-2 py-1 rounded-full border ${style.cls} whitespace-nowrap`}>
                              {o.convenience_score?.toFixed(0)} · {style.label}
                            </div>
                          </div>

                          {(o.structure_name || o.board_type || o.departure_location) && (
                            <div className="text-xs text-slate-600 mb-2">
                              {[o.structure_name, o.board_type, o.departure_location && `da ${o.departure_location}`]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          )}

                          <div className="flex items-end justify-between gap-2 pt-2 border-t border-slate-100">
                            <div>
                              <div className="text-2xl font-bold text-indigo-700">
                                {formatEUR(o.current_price)}
                              </div>
                              {priceDelta !== 0 && (
                                <div className={`text-xs flex items-center gap-1 ${priceDelta < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {priceDelta < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                  {formatEUR(Math.abs(priceDelta))} vs prec. ({formatEUR(o.previous_price)})
                                </div>
                              )}
                              {o.source_name && (
                                <div className="text-[10px] text-slate-400 mt-0.5">Fonte: {o.source_name}</div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              <Button size="sm" variant="outline" onClick={() => setSelectedOffer(o)} data-testid={`view-offer-${o.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openEditOffer(o)} data-testid={`edit-offer-${o.id}`}>
                                <Pencil className="w-3.5 h-3.5 text-blue-600" />
                              </Button>
                              {o.is_hidden ? (
                                <Button size="sm" variant="outline" onClick={() => unhideOffer(o.id)} data-testid={`unhide-offer-${o.id}`}>
                                  <Eye className="w-3.5 h-3.5 text-emerald-600" />
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => hideOffer(o.id)} data-testid={`hide-offer-${o.id}`}>
                                  <EyeOff className="w-3.5 h-3.5 text-slate-500" />
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => deleteOffer(o.id)} data-testid={`delete-offer-${o.id}`}>
                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-indigo-600 hover:bg-indigo-700"
                                onClick={() => applyToQuote(o)}
                                data-testid={`use-in-quote-${o.id}`}
                              >
                                <FileText className="w-3.5 h-3.5 mr-1" /> Usa
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI ASSISTANT TAB */}
          <TabsContent value="ai" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Bot className="w-5 h-5 text-indigo-600" />
                      AI Travel Assistant
                    </CardTitle>
                    <CardDescription>
                      Incolla la richiesta del cliente in linguaggio naturale. L&apos;AI estrarrà i criteri
                      (budget, periodo, partenza, persone…) e proporrà le offerte più coerenti dal Radar
                      con descrizioni persuasive pronte da inviare.
                    </CardDescription>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    <Button
                      variant={aiTab === 'new' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAiTab('new')}
                      data-testid="ai-subtab-new"
                      className={aiTab === 'new' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Nuova
                    </Button>
                    <Button
                      variant={aiTab === 'history' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAiTab('history')}
                      data-testid="ai-subtab-history"
                      className={aiTab === 'history' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                    >
                      Storico ({aiHistory.length})
                    </Button>
                    <Button
                      variant={aiTab === 'analytics' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAiTab('analytics')}
                      data-testid="ai-subtab-analytics"
                      className={aiTab === 'analytics' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                    >
                      <TrendingUp className="w-4 h-4 mr-1" />
                      Analytics
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiTab === 'analytics' ? (
                  <div className="space-y-4" data-testid="ai-analytics">
                    {/* Filtri e export */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 border-b border-slate-100">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Label className="text-xs uppercase tracking-wide text-slate-500 mr-1">Periodo:</Label>
                        {[
                          { v: '7d', l: 'Ultimi 7gg' },
                          { v: '30d', l: 'Ultimi 30gg' },
                          { v: '90d', l: 'Ultimi 90gg' },
                          { v: 'all', l: 'Sempre' },
                        ].map((opt) => (
                          <Button
                            key={opt.v}
                            size="sm"
                            variant={aiAnalyticsPeriod === opt.v ? 'default' : 'outline'}
                            className={aiAnalyticsPeriod === opt.v ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                            onClick={() => setAiAnalyticsPeriod(opt.v)}
                            data-testid={`period-${opt.v}`}
                          >
                            {opt.l}
                          </Button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportAnalyticsCSV}
                          disabled={!aiAnalytics || aiAnalytics.total_requests === 0}
                          data-testid="export-analytics-csv"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={exportAiHistoryExcel}
                          disabled={!aiAnalytics || aiAnalytics.total_requests === 0}
                          data-testid="export-ai-history-excel"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Excel Storico
                        </Button>
                      </div>
                    </div>

                    {!aiAnalytics ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : aiAnalytics.total_requests === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p className="font-medium">Nessun dato da analizzare</p>
                        <p className="text-sm">
                          {aiAnalyticsPeriod === 'all'
                            ? 'Esegui alcune analisi AI per popolare le statistiche.'
                            : 'Nessuna richiesta in questo periodo — prova con "Sempre".'}
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* KPI grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                            <div className="text-xs uppercase text-indigo-600 font-semibold">Richieste totali</div>
                            <div className="text-2xl font-bold text-indigo-900 mt-1">{aiAnalytics.total_requests}</div>
                          </div>
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                            <div className="text-xs uppercase text-emerald-700 font-semibold">Lead caldi</div>
                            <div className="text-2xl font-bold text-emerald-900 mt-1">
                              {aiAnalytics.hot_leads}
                              <span className="text-xs text-emerald-600 ml-2">({aiAnalytics.hot_leads_pct}%)</span>
                            </div>
                            <div className="text-[10px] text-emerald-600 mt-0.5">≥ 4 criteri compilati</div>
                          </div>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="text-xs uppercase text-amber-700 font-semibold">Budget medio</div>
                            <div className="text-2xl font-bold text-amber-900 mt-1">
                              {aiAnalytics.avg_budget ? formatEUR(aiAnalytics.avg_budget) : '—'}
                            </div>
                            {aiAnalytics.median_budget && (
                              <div className="text-[10px] text-amber-600 mt-0.5">
                                mediana {formatEUR(aiAnalytics.median_budget)}
                              </div>
                            )}
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="text-xs uppercase text-blue-700 font-semibold">Match offerte</div>
                            <div className="text-2xl font-bold text-blue-900 mt-1">
                              {aiAnalytics.conversion_with_suggestions_pct}%
                            </div>
                            <div className="text-[10px] text-blue-600 mt-0.5">
                              {aiAnalytics.suggestions_given}/{aiAnalytics.total_requests} con suggerimenti
                            </div>
                          </div>
                        </div>

                        {aiAnalytics.bambini_eta_mancante > 0 && (
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                            <div className="text-sm font-semibold text-yellow-800">
                              ⚠️ {aiAnalytics.bambini_eta_mancante} richieste con bambini ma senza età specificata
                            </div>
                            <div className="text-xs text-yellow-700 mt-0.5">
                              Ricorda di chiedere l&apos;età dei bambini per fare un preventivo corretto.
                            </div>
                          </div>
                        )}

                        {/* Top categories */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div className="border border-slate-200 rounded-lg p-3">
                            <h4 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-1">
                              <Sparkles className="w-4 h-4 text-indigo-600" /> Top Categorie Richieste
                            </h4>
                            {aiAnalytics.top_categories.length === 0 ? (
                              <p className="text-xs text-slate-400">Nessun dato</p>
                            ) : (
                              <div className="space-y-1.5">
                                {aiAnalytics.top_categories.map((c, i) => (
                                  <div key={c.tipologia} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700 capitalize">
                                      <span className="text-slate-400 mr-1">#{i + 1}</span>{c.tipologia}
                                    </span>
                                    <span className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">{c.count}</Badge>
                                      {c.avg_budget && (
                                        <span className="text-[10px] text-slate-500">{formatEUR(c.avg_budget)}</span>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="border border-slate-200 rounded-lg p-3">
                            <h4 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-1">
                              <MapPin className="w-4 h-4 text-blue-600" /> Top Destinazioni
                            </h4>
                            {aiAnalytics.top_destinations.length === 0 ? (
                              <p className="text-xs text-slate-400">Nessun dato</p>
                            ) : (
                              <div className="space-y-1.5">
                                {aiAnalytics.top_destinations.map((d, i) => (
                                  <div key={d.destination} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700 capitalize">
                                      <span className="text-slate-400 mr-1">#{i + 1}</span>{d.destination}
                                    </span>
                                    <Badge variant="outline" className="text-xs">{d.count}</Badge>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="border border-slate-200 rounded-lg p-3">
                            <h4 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-1">
                              <Globe className="w-4 h-4 text-emerald-600" /> Top Partenze
                            </h4>
                            {aiAnalytics.top_departures.length === 0 ? (
                              <p className="text-xs text-slate-400">Nessun dato</p>
                            ) : (
                              <div className="space-y-1.5">
                                {aiAnalytics.top_departures.map((d, i) => (
                                  <div key={d.departure} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-700 capitalize">
                                      <span className="text-slate-400 mr-1">#{i + 1}</span>{d.departure}
                                    </span>
                                    <Badge variant="outline" className="text-xs">{d.count}</Badge>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-xs text-slate-500 italic">
                          💡 Le categorie senza match nel Radar sono opportunità: aggiungi offerte di quelle tipologie per aumentare la conversione.
                        </div>
                      </>
                    )}
                  </div>
                ) : aiTab === 'history' ? (
                  <div className="space-y-3" data-testid="ai-history-list">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Cerca nello storico..."
                        className="pl-9"
                        value={aiHistorySearch}
                        onChange={(e) => setAiHistorySearch(e.target.value)}
                      />
                    </div>
                    {aiHistory.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Bot className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p className="font-medium">Nessuna analisi nello storico</p>
                        <p className="text-sm">Le richieste analizzate dall&apos;AI compariranno qui.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {aiHistory
                          .filter((h) =>
                            !aiHistorySearch.trim() ||
                            [h.client_request, h.analysis]
                              .filter(Boolean)
                              .some((v) => v.toLowerCase().includes(aiHistorySearch.toLowerCase()))
                          )
                          .map((h) => (
                            <div
                              key={h.id}
                              data-testid={`ai-history-row-${h.id}`}
                              className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <Badge variant="secondary" className="text-xs">
                                      {h.suggestion_count || 0} offerte
                                    </Badge>
                                    <span className="text-xs text-slate-500">
                                      {h.user_name} · {formatDate(h.created_at)}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium text-slate-800 line-clamp-2">
                                    {h.client_request}
                                  </p>
                                  {h.analysis && (
                                    <p className="text-xs text-slate-500 mt-1 italic line-clamp-1">
                                      {h.analysis}
                                    </p>
                                  )}
                                  {h.extracted_criteria && Object.keys(h.extracted_criteria).length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {Object.entries(h.extracted_criteria)
                                        .filter(([, v]) => v !== null && v !== '' && v !== undefined)
                                        .slice(0, 5)
                                        .map(([k, v]) => (
                                          <Badge key={k} variant="outline" className="text-[10px]">
                                            {k}: {String(v).slice(0, 20)}
                                          </Badge>
                                        ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => loadAiHistoryDetail(h.id)}
                                    data-testid={`ai-history-reopen-${h.id}`}
                                    title="Riapri analisi"
                                  >
                                    <RefreshCw className="w-3.5 h-3.5 text-indigo-600" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => deleteAiHistory(h.id)}
                                    data-testid={`ai-history-delete-${h.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                <Textarea
                  rows={4}
                  placeholder={`Esempio:\n"Vorrei partire ad agosto, siamo 2 adulti e 1 bambino, budget massimo 2.500€, mare, partenza da Roma."`}
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  data-testid="ai-query-input"
                />
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" />
                    Powered by Claude Sonnet 4.6 · risponde in italiano · storico salvato automaticamente
                  </div>
                  <Button
                    onClick={askAI}
                    disabled={aiLoading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="ai-ask-btn"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    {aiLoading ? 'Sto analizzando...' : 'Analizza richiesta'}
                  </Button>
                </div>

                {aiLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                  </div>
                )}

                {aiResult && !aiLoading && (
                  <div className="space-y-4 mt-2" data-testid="ai-result">
                    {/* Children ages missing alert */}
                    {aiResult.extracted_criteria?.bambini_eta_mancante && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded" data-testid="bambini-eta-alert">
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-sm font-semibold text-yellow-800">
                              ⚠️ Mancano le età dei bambini
                            </div>
                            <p className="text-xs text-yellow-700 mt-1">
                              Per fare un preventivo corretto è necessario conoscere l&apos;età esatta di ciascun bambino
                              (le tariffe variano per fascia: 0-2 anni, 3-11 anni, 12+ anni).
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Suggested questions */}
                    {aiResult.domande_da_fare_al_cliente?.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="suggested-questions">
                        <div className="text-xs font-semibold text-blue-700 uppercase mb-2 flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" /> Domande da fare al cliente
                        </div>
                        <ul className="space-y-1">
                          {aiResult.domande_da_fare_al_cliente.map((q, i) => (
                            <li key={i} className="text-sm text-blue-900 flex items-start gap-2">
                              <span className="text-blue-400 flex-shrink-0">•</span>
                              <span>{q}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Analysis box */}
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <Bot className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">
                            Analisi della richiesta
                          </div>
                          <p className="text-sm text-slate-800 mt-1">
                            {aiResult.analysis || 'Nessuna analisi'}
                          </p>
                          {aiResult.extracted_criteria && Object.keys(aiResult.extracted_criteria).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {Object.entries(aiResult.extracted_criteria)
                                .filter(([, v]) => v !== null && v !== '' && v !== undefined)
                                .map(([k, v]) => (
                                  <Badge key={k} variant="outline" className="text-xs bg-white">
                                    <span className="text-slate-500 mr-1">{k}:</span>
                                    <span className="font-medium">{String(v)}</span>
                                  </Badge>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Suggestions */}
                    {aiResult.suggestions?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Sparkles className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">Nessuna offerta nel Radar corrisponde alla richiesta.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="text-sm font-semibold text-slate-700">
                          Offerte suggerite ({aiResult.suggestions?.length || 0})
                        </div>
                        {aiResult.suggestions.map((s, idx) => {
                          const o = s.offer || {};
                          const style = SCORE_STYLES[o.convenience_label] || SCORE_STYLES.average;
                          return (
                            <div
                              key={s.offer_id}
                              data-testid={`ai-suggestion-${idx}`}
                              className="border border-slate-200 rounded-lg p-4 bg-white"
                            >
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                                      Match {s.match_score ?? '—'}/100
                                    </Badge>
                                    <h4 className="font-semibold text-slate-800 truncate">{o.title}</h4>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                                    <Badge variant="secondary">{o.category_label}</Badge>
                                    {o.destination && <span>📍 {o.destination}</span>}
                                    {o.duration_nights && <span>{o.duration_nights} notti</span>}
                                    {o.departure_date && <span>🗓️ {formatDate(o.departure_date)}</span>}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="text-2xl font-bold text-indigo-700">{formatEUR(o.current_price)}</div>
                                  <div className={`text-[10px] px-2 py-0.5 rounded-full border ${style.cls} inline-block mt-1`}>
                                    {style.label}
                                  </div>
                                </div>
                              </div>

                              {s.reason && (
                                <div className="bg-amber-50 border-l-2 border-amber-400 px-3 py-2 rounded mb-2">
                                  <div className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                                    <Lightbulb className="w-3 h-3" />
                                    Perché è ideale
                                  </div>
                                  <p className="text-xs text-amber-900">{s.reason}</p>
                                </div>
                              )}

                              {s.persuasive_description && (
                                <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-2">
                                  <div className="text-xs font-semibold text-slate-600 mb-1">
                                    Descrizione persuasiva (pronta da copiare)
                                  </div>
                                  <p className="text-sm text-slate-800 italic whitespace-pre-wrap">
                                    {s.persuasive_description}
                                  </p>
                                </div>
                              )}

                              <div className="flex flex-wrap gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(s.persuasive_description || '');
                                    toast.success('Descrizione copiata');
                                  }}
                                >
                                  Copia descrizione
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-indigo-600 hover:bg-indigo-700"
                                  onClick={() => applyToQuote(o)}
                                  data-testid={`ai-use-${idx}`}
                                >
                                  <FileText className="w-3.5 h-3.5 mr-1" />
                                  Usa nel Preventivo
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ALERTS TAB */}
          <TabsContent value="alerts" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-amber-500" />
                      Alert Clienti
                    </CardTitle>
                    <CardDescription>
                      Crea alert per i clienti su prezzo/categoria/destinazione. Quando un&apos;offerta corrisponde,
                      genera un messaggio WhatsApp pronto da inviare.
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={runAlertCheck} data-testid="run-alert-check-btn">
                      <RefreshCw className="w-4 h-4 mr-1" /> Verifica match
                    </Button>
                    <Button onClick={openCreateAlert} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-alert-btn">
                      <Plus className="w-4 h-4 mr-1" /> Nuovo Alert
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="alerts-empty">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Nessun alert configurato</p>
                    <p className="text-sm">Crea un alert per ricevere automaticamente match con le offerte.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {alerts.map((a) => {
                      const matchData = alertMatches[a.id];
                      const matchCount = matchData?.match_count || 0;
                      const catLabel = a.category
                        ? categories.find((c) => c.value === a.category)?.label || a.category
                        : 'Qualsiasi';
                      return (
                        <div
                          key={a.id}
                          data-testid={`alert-row-${a.id}`}
                          className={`border rounded-lg p-3 transition-colors ${
                            matchCount > 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-slate-800">{a.client_name}</span>
                                <Badge variant={a.active ? 'default' : 'outline'} className="text-xs">
                                  {a.active ? 'Attivo' : 'Pausa'}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">{catLabel}</Badge>
                                <Badge variant="outline" className="text-xs">
                                  Max {formatEUR(a.max_price)}
                                </Badge>
                                {matchCount > 0 && (
                                  <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600 animate-pulse">
                                    {matchCount} match!
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-2">
                                {a.client_phone && (
                                  <span className="flex items-center gap-1">
                                    <MessageCircle className="w-3 h-3" />{a.client_phone}
                                  </span>
                                )}
                                {a.destination_contains && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />contiene &quot;{a.destination_contains}&quot;
                                  </span>
                                )}
                                {a.departure_from_date && (
                                  <span>dal {formatDate(a.departure_from_date)}</span>
                                )}
                                {a.departure_to_date && (
                                  <span>al {formatDate(a.departure_to_date)}</span>
                                )}
                              </div>
                              {a.notes && (
                                <div className="text-xs text-slate-500 italic mt-1">{a.notes}</div>
                              )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                size="sm"
                                variant={matchCount > 0 ? 'default' : 'outline'}
                                className={matchCount > 0 ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                                onClick={() => openMatchesDialog(a)}
                                disabled={matchCount === 0}
                                data-testid={`view-matches-${a.id}`}
                              >
                                <Sparkles className="w-3.5 h-3.5 mr-1" />
                                {matchCount}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => toggleAlert(a)} title={a.active ? 'Pausa' : 'Riattiva'}>
                                <Power className={`w-4 h-4 ${a.active ? 'text-emerald-600' : 'text-slate-400'}`} />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openEditAlert(a)}>
                                <Pencil className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => deleteAlert(a.id)}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SOURCES TAB */}
          <TabsContent value="sources" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <CardTitle>Fonti / URL Monitorate</CardTitle>
                    <CardDescription>
                      Lista dei siti dai quali il sistema cercherà offerte (scraping verrà attivato in fase avanzata)
                    </CardDescription>
                  </div>
                  <Button onClick={openCreateSource} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-source-btn">
                    <Plus className="w-4 h-4 mr-1" /> Nuova Fonte
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {sources.length === 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="sources-empty">
                    <Globe className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Nessuna fonte configurata</p>
                    <p className="text-sm">Aggiungi gli URL dei siti che vuoi monitorare.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sources.map((s) => {
                      const catLabel = categories.find((c) => c.value === s.category)?.label || s.category;
                      return (
                        <div key={s.id} className="flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                             data-testid={`source-row-${s.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Globe className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                              <span className="font-medium text-slate-800 truncate">{s.name}</span>
                              <Badge variant="secondary" className="text-xs">{catLabel}</Badge>
                              <Badge variant={s.enabled ? 'default' : 'outline'} className="text-xs">
                                {s.enabled ? 'Attiva' : 'Disattivata'}
                              </Badge>
                              <Badge variant="outline" className="text-xs">{s.frequency_hours}h</Badge>
                            </div>
                            <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline break-all flex items-center gap-1 mt-1">
                              {s.url}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            {s.notes && <div className="text-xs text-slate-500 mt-1">{s.notes}</div>}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="outline" onClick={() => toggleSource(s)} title={s.enabled ? 'Disattiva' : 'Attiva'}>
                              <Power className={`w-4 h-4 ${s.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openEditSource(s)}>
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteSource(s.id)}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* IMPORT TAB */}
          <TabsContent value="import" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Importa Offerte (JSON)</CardTitle>
                <CardDescription>
                  Incolla un array JSON di offerte. Esistenti con lo stesso titolo+URL vengono aggiornate
                  (con cronologia prezzi automatica).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  rows={12}
                  placeholder={`[
  {
    "title": "Crociera Mediterraneo",
    "category": "crociere",
    "destination": "Med. Occidentale",
    "current_price": 799,
    "duration_nights": 7,
    "source_name": "Costa Crociere",
    "source_url": "https://...",
    "departure_date": "2026-07-15",
    "departure_location": "Civitavecchia"
  }
]`}
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  className="font-mono text-xs"
                  data-testid="import-json-textarea"
                />
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs text-slate-500">
                    Categorie disponibili: {categories.map((c) => c.value).join(', ')}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setImportText('')}>Pulisci</Button>
                    <Button onClick={handleImport} disabled={importing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="import-btn">
                      <Upload className="w-4 h-4 mr-1" />
                      {importing ? 'Importazione...' : 'Importa'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Source dialog */}
      <Dialog open={showSourceDialog} onOpenChange={setShowSourceDialog}>
        <DialogContent className="max-w-lg" data-testid="source-dialog">
          <DialogHeader>
            <DialogTitle>{editingSource ? 'Modifica Fonte' : 'Nuova Fonte URL'}</DialogTitle>
            <DialogDescription>Configura un sito da monitorare per le offerte</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={sourceForm.name} onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })} placeholder="Es. Costa Crociere - Offerte" data-testid="source-name" />
            </div>
            <div>
              <Label>URL *</Label>
              <Input value={sourceForm.url} onChange={(e) => setSourceForm({ ...sourceForm, url: e.target.value })} placeholder="https://..." data-testid="source-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria *</Label>
                <Select value={sourceForm.category} onValueChange={(v) => setSourceForm({ ...sourceForm, category: v })}>
                  <SelectTrigger data-testid="source-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequenza (ore)</Label>
                <Select
                  value={String(sourceForm.frequency_hours)}
                  onValueChange={(v) => setSourceForm({ ...sourceForm, frequency_hours: parseInt(v, 10) })}
                >
                  <SelectTrigger data-testid="source-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">Ogni 2 ore (alta priorità)</SelectItem>
                    <SelectItem value="6">Ogni 6 ore (last minute)</SelectItem>
                    <SelectItem value="24">Ogni 24 ore (standard)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={sourceForm.notes} onChange={(e) => setSourceForm({ ...sourceForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSourceDialog(false)}>Annulla</Button>
            <Button onClick={saveSource} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-source-btn">
              {editingSource ? 'Salva' : 'Aggiungi Fonte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer create/edit dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader>
            <DialogTitle>{editingOffer ? 'Modifica Offerta' : 'Nuova Offerta'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Titolo *</Label>
              <Input value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} data-testid="offer-title" />
            </div>
            <div>
              <Label>Categoria *</Label>
              <Select value={offerForm.category} onValueChange={(v) => setOfferForm({ ...offerForm, category: v })}>
                <SelectTrigger data-testid="offer-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destinazione</Label>
              <Input value={offerForm.destination} onChange={(e) => setOfferForm({ ...offerForm, destination: e.target.value })} />
            </div>
            <div>
              <Label>Prezzo attuale (€) *</Label>
              <Input type="number" step="0.01" value={offerForm.current_price} onChange={(e) => setOfferForm({ ...offerForm, current_price: e.target.value })} data-testid="offer-price" />
            </div>
            <div>
              <Label>Notti</Label>
              <Input type="number" value={offerForm.duration_nights} onChange={(e) => setOfferForm({ ...offerForm, duration_nights: e.target.value })} />
            </div>
            <div>
              <Label>Data partenza</Label>
              <Input type="date" value={offerForm.departure_date} onChange={(e) => setOfferForm({ ...offerForm, departure_date: e.target.value })} />
            </div>
            <div>
              <Label>Data rientro</Label>
              <Input type="date" value={offerForm.return_date} onChange={(e) => setOfferForm({ ...offerForm, return_date: e.target.value })} />
            </div>
            <div>
              <Label>Partenza da (aeroporto/porto)</Label>
              <Input value={offerForm.departure_location} onChange={(e) => setOfferForm({ ...offerForm, departure_location: e.target.value })} />
            </div>
            <div>
              <Label>Struttura/Nave/Hotel</Label>
              <Input value={offerForm.structure_name} onChange={(e) => setOfferForm({ ...offerForm, structure_name: e.target.value })} />
            </div>
            <div>
              <Label>Trattamento</Label>
              <Input value={offerForm.board_type} onChange={(e) => setOfferForm({ ...offerForm, board_type: e.target.value })} placeholder="Es. All Inclusive" />
            </div>
            <div>
              <Label>Disponibilità</Label>
              <Input value={offerForm.availability} onChange={(e) => setOfferForm({ ...offerForm, availability: e.target.value })} />
            </div>
            <div>
              <Label>Nome fonte</Label>
              <Input value={offerForm.source_name} onChange={(e) => setOfferForm({ ...offerForm, source_name: e.target.value })} />
            </div>
            <div>
              <Label>URL fonte</Label>
              <Input value={offerForm.source_url} onChange={(e) => setOfferForm({ ...offerForm, source_url: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Immagine URL</Label>
              <Input value={offerForm.image_url} onChange={(e) => setOfferForm({ ...offerForm, image_url: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Condizioni / Note</Label>
              <Textarea rows={2} value={offerForm.conditions} onChange={(e) => setOfferForm({ ...offerForm, conditions: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferDialog(false)}>Annulla</Button>
            <Button onClick={saveOffer} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-offer-btn">
              {editingOffer ? 'Salva' : 'Aggiungi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer detail dialog */}
      <Dialog open={!!selectedOffer} onOpenChange={(o) => !o && setSelectedOffer(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedOffer && (() => {
            const style = SCORE_STYLES[selectedOffer.convenience_label] || SCORE_STYLES.average;
            const catLabel = categories.find((c) => c.value === selectedOffer.category)?.label || selectedOffer.category;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {selectedOffer.title}
                  </DialogTitle>
                  <DialogDescription>
                    <Badge variant="secondary" className="mr-2">{catLabel}</Badge>
                    {selectedOffer.destination}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className={`p-3 rounded-lg border ${style.cls}`}>
                    <div className="text-xs uppercase tracking-wide font-semibold">Convenienza</div>
                    <div className="text-2xl font-bold">{selectedOffer.convenience_score?.toFixed(1)} / 100 — {style.label}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><strong>Prezzo:</strong> {formatEUR(selectedOffer.current_price)}</div>
                    {selectedOffer.previous_price != null && (
                      <div><strong>Prezzo prec.:</strong> {formatEUR(selectedOffer.previous_price)}</div>
                    )}
                    {selectedOffer.duration_nights && <div><strong>Notti:</strong> {selectedOffer.duration_nights}</div>}
                    {selectedOffer.departure_date && <div><strong>Partenza:</strong> {formatDate(selectedOffer.departure_date)}</div>}
                    {selectedOffer.return_date && <div><strong>Rientro:</strong> {formatDate(selectedOffer.return_date)}</div>}
                    {selectedOffer.departure_location && <div><strong>Da:</strong> {selectedOffer.departure_location}</div>}
                    {selectedOffer.structure_name && <div><strong>Struttura:</strong> {selectedOffer.structure_name}</div>}
                    {selectedOffer.board_type && <div><strong>Trattamento:</strong> {selectedOffer.board_type}</div>}
                    {selectedOffer.source_name && <div><strong>Fonte:</strong> {selectedOffer.source_name}</div>}
                  </div>
                  {selectedOffer.conditions && (
                    <div className="text-sm text-slate-600">
                      <strong>Condizioni:</strong> {selectedOffer.conditions}
                    </div>
                  )}
                  {selectedOffer.source_url && (
                    <a href={selectedOffer.source_url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                      Apri pagina sorgente <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSelectedOffer(null)}>Chiudi</Button>
                  <Button onClick={() => { applyToQuote(selectedOffer); }} className="bg-indigo-600 hover:bg-indigo-700">
                    <FileText className="w-4 h-4 mr-1" /> Usa nel Preventivo
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Alert Create/Edit Dialog */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="alert-dialog">
          <DialogHeader>
            <DialogTitle>{editingAlert ? 'Modifica Alert' : 'Nuovo Alert Cliente'}</DialogTitle>
            <DialogDescription>
              Quando un&apos;offerta soddisfa i criteri verrà generato un messaggio WhatsApp pronto da inviare.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Nome cliente *</Label>
              <Input value={alertForm.client_name} onChange={(e) => setAlertForm({ ...alertForm, client_name: e.target.value })} data-testid="alert-client-name" />
            </div>
            <div>
              <Label>Telefono (per WhatsApp)</Label>
              <Input value={alertForm.client_phone} onChange={(e) => setAlertForm({ ...alertForm, client_phone: e.target.value })} placeholder="+39 333 1234567" data-testid="alert-client-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={alertForm.client_email} onChange={(e) => setAlertForm({ ...alertForm, client_email: e.target.value })} />
            </div>
            <div>
              <Label>Prezzo massimo (€) *</Label>
              <Input type="number" step="0.01" value={alertForm.max_price} onChange={(e) => setAlertForm({ ...alertForm, max_price: e.target.value })} placeholder="800" data-testid="alert-max-price" />
            </div>
            <div>
              <Label>Categoria (opzionale)</Label>
              <Select value={alertForm.category || 'all'} onValueChange={(v) => setAlertForm({ ...alertForm, category: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="alert-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualsiasi categoria</SelectItem>
                  {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destinazione contiene</Label>
              <Input value={alertForm.destination_contains} onChange={(e) => setAlertForm({ ...alertForm, destination_contains: e.target.value })} placeholder="Es. Sharm, Disney..." />
            </div>
            <div>
              <Label>Partenza dal</Label>
              <Input type="date" value={alertForm.departure_from_date} onChange={(e) => setAlertForm({ ...alertForm, departure_from_date: e.target.value })} />
            </div>
            <div>
              <Label>Partenza al</Label>
              <Input type="date" value={alertForm.departure_to_date} onChange={(e) => setAlertForm({ ...alertForm, departure_to_date: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Note interne</Label>
              <Textarea rows={2} value={alertForm.notes} onChange={(e) => setAlertForm({ ...alertForm, notes: e.target.value })} />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="alert-active"
                checked={alertForm.active}
                onChange={(e) => setAlertForm({ ...alertForm, active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="alert-active" className="cursor-pointer">Alert attivo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertDialog(false)}>Annulla</Button>
            <Button onClick={saveAlert} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-alert-btn">
              {editingAlert ? 'Salva' : 'Crea Alert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Matches Dialog */}
      <Dialog open={!!matchesDialog} onOpenChange={(o) => !o && setMatchesDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="matches-dialog">
          {matchesDialog && (() => {
            const data = alertMatches[matchesDialog.id];
            const matches = data?.matches || [];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                    {matches.length} match per {matchesDialog.client_name}
                  </DialogTitle>
                  <DialogDescription>
                    Clicca su &quot;Invia WhatsApp&quot; per aprire il messaggio precompilato pronto da inviare.
                  </DialogDescription>
                </DialogHeader>
                {matches.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">Nessun match al momento.</div>
                ) : (
                  <div className="space-y-3">
                    {matches.map((m, idx) => {
                      const o = m.offer;
                      const style = SCORE_STYLES[o.convenience_label] || SCORE_STYLES.average;
                      return (
                        <div key={o.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`match-row-${idx}`}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0 flex-1">
                              <h4 className="font-semibold text-slate-800">{o.title}</h4>
                              <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-1">
                                {o.destination && <span>📍 {o.destination}</span>}
                                {o.duration_nights && <span>{o.duration_nights} notti</span>}
                                {o.departure_date && <span>🗓️ {formatDate(o.departure_date)}</span>}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-indigo-700">{formatEUR(o.current_price)}</div>
                              <Badge className={`text-xs mt-1 ${style.cls}`} variant="outline">{style.label}</Badge>
                            </div>
                          </div>
                          <details className="text-xs text-slate-600 mb-2">
                            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                              Anteprima messaggio WhatsApp
                            </summary>
                            <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap font-sans">
{m.whatsapp_message}
                            </pre>
                          </details>
                          <div className="flex flex-wrap gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(m.whatsapp_message);
                                toast.success('Messaggio copiato');
                              }}
                            >
                              Copia
                            </Button>
                            {m.whatsapp_url ? (
                              <a href={m.whatsapp_url} target="_blank" rel="noreferrer">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid={`send-whatsapp-${idx}`}>
                                  <MessageCircle className="w-4 h-4 mr-1" /> Invia WhatsApp
                                </Button>
                              </a>
                            ) : (
                              <Button size="sm" disabled title="Telefono mancante">
                                Telefono mancante
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMatchesDialog(null)}>Chiudi</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TravelRadar;
